/**
 * Ticket Type Icon Component
 * Displays an appropriate icon for each ticket type
 */

import { Box, Tooltip } from '@mui/material'
import {
  BugReport,
  Lightbulb,
  HelpOutline,
  Build,
  Person,
  Flag,
  Warning,
  MoreHoriz,
  Report,
  Map,
  Groups,
  Event,
  EmojiEvents,
  Home,
  LocationOff,
  Block,
  HealthAndSafety,
} from '@mui/icons-material'
import { TicketType, TicketTypeLabels } from '@api/types/tickets'

interface TicketTypeIconProps {
  type: TicketType
  size?: 'small' | 'medium' | 'large'
  showLabel?: boolean
  color?: string
}

const typeIcons: Record<TicketType, React.ReactNode> = {
  [TicketType.BugReport]: <BugReport />,
  [TicketType.FeatureRequest]: <Lightbulb />,
  [TicketType.QuestionSupport]: <HelpOutline />,
  [TicketType.TechnicalIssue]: <Build />,
  [TicketType.AccountIssue]: <Person />,
  [TicketType.ContentInappropriate]: <Flag />,
  [TicketType.ContentMisleading]: <Warning />,
  [TicketType.ContentSpam]: <Report />,
  [TicketType.ContentHarassment]: <Warning />,
  [TicketType.LocationPrivateProperty]: <Home />,
  [TicketType.LocationMissing]: <LocationOff />,
  [TicketType.LocationInaccessible]: <Block />,
  [TicketType.LocationSafety]: <HealthAndSafety />,
  [TicketType.AdventureIssue]: <Map />,
  [TicketType.LocationIssue]: <Map />,
  [TicketType.GroupIssue]: <Groups />,
  [TicketType.EventIssue]: <Event />,
  [TicketType.BadgeIssue]: <EmojiEvents />,
  [TicketType.Other]: <MoreHoriz />,
}

const typeColors: Record<TicketType, string> = {
  [TicketType.BugReport]: '#f44336',
  [TicketType.FeatureRequest]: '#ffeb3b',
  [TicketType.QuestionSupport]: '#2196f3',
  [TicketType.TechnicalIssue]: '#ff9800',
  [TicketType.AccountIssue]: '#9c27b0',
  [TicketType.ContentInappropriate]: '#e91e63',
  [TicketType.ContentMisleading]: '#ff5722',
  [TicketType.ContentSpam]: '#d32f2f',
  [TicketType.ContentHarassment]: '#c62828',
  [TicketType.LocationPrivateProperty]: '#795548',
  [TicketType.LocationMissing]: '#607d8b',
  [TicketType.LocationInaccessible]: '#ff5722',
  [TicketType.LocationSafety]: '#d32f2f',
  [TicketType.AdventureIssue]: '#1976d2',
  [TicketType.LocationIssue]: '#388e3c',
  [TicketType.GroupIssue]: '#7b1fa2',
  [TicketType.EventIssue]: '#f57c00',
  [TicketType.BadgeIssue]: '#fbc02d',
  [TicketType.Other]: '#757575',
}

const sizeMap = {
  small: 18,
  medium: 24,
  large: 32,
}

export function TicketTypeIcon({ type, size = 'medium', showLabel = false, color }: TicketTypeIconProps) {
  const label = TicketTypeLabels[type]
  const iconColor = color || typeColors[type]
  const iconSize = sizeMap[size]

  const icon = (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: showLabel ? 1 : 0,
        color: iconColor,
        '& svg': {
          fontSize: iconSize,
        },
      }}
      aria-label={`Type: ${label}`}
    >
      {typeIcons[type]}
      {showLabel && <span>{label}</span>}
    </Box>
  )

  if (showLabel) {
    return icon
  }

  return (
    <Tooltip title={label} arrow>
      {icon}
    </Tooltip>
  )
}
